import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { Radio, Zap, ScanSearch, FileText, ArrowRight, Loader2, AlertCircle, List, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { analysisModeAtom, escalateIfPromisingAtom } from '@/hooks/atoms';
import { useCreateJob } from '@/hooks/useJob';
import type { AnalysisMode } from '@/types';
import { cn } from '@/lib/utils';

const ANALYSIS_MODES: {
  value: AnalysisMode;
  label: string;
  description: string;
  icon: typeof Zap;
  recommended?: boolean;
}[] = [
  {
    value: 'fast_scan',
    label: 'Fast Scan',
    description: 'Quick headline-level check. Samples key segments only.',
    icon: Zap,
  },
  {
    value: 'adaptive',
    label: 'Adaptive',
    description: 'Starts fast, escalates if promising content is detected.',
    icon: ScanSearch,
    recommended: true,
  },
  {
    value: 'full',
    label: 'Full Transcript',
    description: 'Comprehensive analysis of the entire transcript.',
    icon: FileText,
  },
];

function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

export function SubmitPage() {
  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState<'single' | 'batch'>('single');
  const [url, setUrl] = useState('');
  const [batchUrls, setBatchUrls] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [mode, setMode] = useAtom(analysisModeAtom);
  const [escalate, setEscalate] = useAtom(escalateIfPromisingAtom);
  const { creating, error: createError, createJob } = useCreateJob();
  const [batchProgress, setBatchProgress] = useState<{ total: number; done: number; ids: string[] } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError(null);

    if (inputMode === 'single') {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) {
        setUrlError('Please enter a podcast URL.');
        return;
      }
      if (!isValidUrl(trimmedUrl)) {
        setUrlError('Please enter a valid URL (e.g., https://example.com/podcast).');
        return;
      }

      try {
        const result = await createJob(trimmedUrl, mode, escalate);
        navigate(`/jobs/${result.id}`);
      } catch {
        // Error handled by hook
      }
    } else {
      // Batch mode
      const lines = batchUrls
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      if (lines.length === 0) {
        setUrlError('Please enter at least one URL.');
        return;
      }

      const invalidLines = lines.filter((l) => !isValidUrl(l));
      if (invalidLines.length > 0) {
        setUrlError(
          `${invalidLines.length} invalid URL${invalidLines.length > 1 ? 's' : ''} found. Each line must be a valid URL.`,
        );
        return;
      }

      setBatchProgress({ total: lines.length, done: 0, ids: [] });

      // Fire all submissions in parallel — each job starts immediately on the server
      const results = await Promise.allSettled(lines.map((u) => createJob(u, mode, escalate)));

      const ids: string[] = [];
      let done = 0;
      for (const r of results) {
        done++;
        if (r.status === 'fulfilled') ids.push(r.value.id);
        setBatchProgress({ total: lines.length, done, ids: [...ids] });
      }

      setBatchProgress(null);

      if (ids.length > 0) {
        navigate('/dashboard');
      }
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Radio className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Podcast News Analyzer</h1>
          </div>
          <p className="text-muted-foreground text-lg">
            Submit a podcast episode for automated newsworthiness analysis and editorial triage.
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>New Analysis</CardTitle>
                <CardDescription>Provide podcast URL{inputMode === 'batch' ? 's' : ''} to begin analysis.</CardDescription>
              </div>
              <div className="flex rounded-md border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setInputMode('single')}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors',
                    inputMode === 'single' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-accent',
                  )}
                >
                  <Link2 className="h-3 w-3" />
                  Single
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('batch')}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors',
                    inputMode === 'batch' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-accent',
                  )}
                >
                  <List className="h-3 w-3" />
                  Batch
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* URL Input */}
              <div className="space-y-2">
                <label htmlFor="podcast-url" className="text-sm font-medium">
                  {inputMode === 'single' ? 'Podcast URL' : 'Podcast URLs (one per line)'}
                </label>
                {inputMode === 'single' ? (
                  <Input
                    id="podcast-url"
                    type="url"
                    placeholder="https://youtu.be/example"
                    value={url}
                    onChange={(e) => {
                      setUrl(e.target.value);
                      if (urlError) setUrlError(null);
                    }}
                    className={cn(urlError && 'border-destructive focus-visible:ring-destructive')}
                    disabled={creating}
                  />
                ) : (
                  <textarea
                    id="podcast-url"
                    placeholder={'https://youtu.be/example1\nhttps://youtu.be/example2\nhttps://youtu.be/example3'}
                    value={batchUrls}
                    onChange={(e) => {
                      setBatchUrls(e.target.value);
                      if (urlError) setUrlError(null);
                    }}
                    rows={5}
                    className={cn(
                      'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                      'ring-offset-background placeholder:text-muted-foreground',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-50 resize-none font-mono',
                      urlError && 'border-destructive focus-visible:ring-destructive',
                    )}
                    disabled={creating}
                  />
                )}
                {urlError && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    {urlError}
                  </p>
                )}
                {inputMode === 'batch' && batchUrls.trim() && (
                  <p className="text-xs text-muted-foreground">
                    {batchUrls.split('\n').filter((l) => l.trim()).length} URL
                    {batchUrls.split('\n').filter((l) => l.trim()).length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              {/* Analysis Mode */}
              <div className="space-y-3">
                <label className="text-sm font-medium">Analysis Mode</label>
                <div className="grid gap-3">
                  {ANALYSIS_MODES.map(({ value, label, description, icon: Icon, recommended }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setMode(value)}
                      disabled={creating}
                      className={cn(
                        'relative flex items-start gap-4 rounded-lg border p-4 text-left transition-colors hover:bg-accent/50',
                        mode === value ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border',
                      )}
                    >
                      <div
                        className={cn(
                          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                          mode === value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{label}</span>
                          {recommended && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              Recommended
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
                      </div>
                      <div
                        className={cn(
                          'mt-1 h-4 w-4 shrink-0 rounded-full border-2',
                          mode === value ? 'border-primary bg-primary' : 'border-muted-foreground/30',
                        )}
                      >
                        {mode === value && (
                          <div className="h-full w-full rounded-full border-2 border-primary-foreground scale-50" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Escalation Toggle */}
              {mode === 'adaptive' && (
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <p className="text-sm font-medium">Escalate if promising</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically run deeper analysis when initial scan detects newsworthy content.
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={escalate}
                    onClick={() => setEscalate(!escalate)}
                    disabled={creating}
                    className={cn(
                      'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                      escalate ? 'bg-primary' : 'bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform',
                        escalate ? 'translate-x-5' : 'translate-x-0',
                      )}
                    />
                  </button>
                </div>
              )}

              {/* Error */}
              {createError && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                  <p className="text-sm text-destructive flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {createError}
                  </p>
                </div>
              )}

              {/* Batch progress */}
              {batchProgress && (
                <div className="rounded-lg border p-4 space-y-2">
                  <p className="text-sm font-medium">
                    Submitting {batchProgress.done} of {batchProgress.total} URLs...
                  </p>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${(batchProgress.done / batchProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Submit */}
              <Button type="submit" size="lg" className="w-full" disabled={creating || !!batchProgress}>
                {creating || batchProgress ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {batchProgress ? `Submitting ${batchProgress.done}/${batchProgress.total}...` : 'Submitting...'}
                  </>
                ) : (
                  <>
                    {inputMode === 'batch' ? 'Analyze All Episodes' : 'Analyze Episode'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
